import { useMemo } from "react";
import { Card, Text } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import AddressFields from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields";
import IndividualFormFields from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/IndividualFields";
import { BackButtonWithinForm } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/BackButtonWithinForm";
import {
  FIELD_MAPPING,
  INITIAL_VALUES,
} from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm/constants";
import { getSchema } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm/schema";
import { type FormValues } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm/types";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { toISOFormat } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import { useInvestorWizard } from "../../context";
import { ExistingProfileBanner } from "../existingProfile/ExistingProfileBanner";
import { addExistingProfileTaxIdCheck } from "../existingProfile/schema";
import { TaxIdMatchingLabel } from "../existingProfile/TaxIdMatchingLabel";
import { useExistingProfileState } from "../existingProfile/useExistingProfileState";
import { ProfileFormFooter } from "../ProfileFormFooter";
import { mergeExtractedData, parseExtractedDate, toWizardAddress } from "../utils";

interface Props {
  onBack: () => void;
  extractedData?: Record<string, string> | null;
  documentId?: number;
  onViewExistingProfile?: (taxId: string) => void;
}

export const IndividualProfileForm = ({
  onBack,
  extractedData,
  documentId,
  onViewExistingProfile,
}: Props) => {
  const { setNewProfileData, setProfileDraft, goNext, profileDraft, caseInfo } =
    useInvestorWizard();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const isPrefilled = Boolean(extractedData) || Boolean(documentId);
  const arenaAccessId =
    ioReusableProfilesEnabled && caseInfo?.clientId ? toDbId(caseInfo.clientId) : undefined;
  const { existingProfile, scheduleCheck } = useExistingProfileState();

  const validationSchema = useMemo(
    () =>
      addExistingProfileTaxIdCheck(getSchema(false, false), { arenaAccessId, scheduleCheck }),
    [arenaAccessId, scheduleCheck],
  );

  const draftValues =
    profileDraft?.category === ComplianceObjectType.Individual
      ? (profileDraft.values as Partial<FormValues>)
      : undefined;

  const initialValues = useMemo(() => {
    const merged = {
      ...mergeExtractedData(INITIAL_VALUES, FIELD_MAPPING, extractedData),
      ...draftValues,
    };
    return { ...merged, dateOfBirth: parseExtractedDate(merged.dateOfBirth) };
  }, [extractedData, draftValues]);

  const handleSubmit = (values: Partial<FormValues>) => {
    setProfileDraft({
      category: ComplianceObjectType.Individual,
      documentId,
      isPrefilled,
      values,
    });
    setNewProfileData({
      category: ComplianceObjectType.Individual,
      accountType: values.accountType,
      creationMethod: isPrefilled ? "document" : "manual",
      documentId,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth ? toISOFormat(values.dateOfBirth) : undefined,
      taxId: values.taxId,
      taxIdType: values.taxIdType ?? undefined,
      address: toWizardAddress(values),
    });
    goNext();
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      submitOnlyChangedValues={false}
    >
      <Card>
        <BackButtonWithinForm onClick={onBack} displayText="Create method" />
        <Text marginBottom="md" variant="h3">
          New individual
        </Text>
        {existingProfile && (
          <ExistingProfileBanner
            taxIdType={existingProfile.taxIdType}
            onViewExistingProfile={
              onViewExistingProfile
                ? () => onViewExistingProfile(existingProfile.taxId)
                : undefined
            }
          />
        )}
        <IndividualFormFields
          aiExtractionSupported={isPrefilled}
          taxIdWarning={
            existingProfile ? (
              <TaxIdMatchingLabel taxIdType={existingProfile.taxIdType} />
            ) : undefined
          }
        />
        <AddressFields aiExtractionSupported={isPrefilled} />
      </Card>
      <ProfileFormFooter onBack={onBack} />
    </Form>
  );
};
